import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/lib/auth-context";

const searchSchema = z.object({
  mode: z.enum(["login", "register"]).optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/auth")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Sign in or join — Quibblies" },
      {
        name: "description",
        content:
          "Create a Quibblies account to publish original fiction, follow authors and build your reading library.",
      },
      { property: "og:title", content: "Sign in or join — Quibblies" },
      {
        property: "og:description",
        content: "Join the Quibblies writing community.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AuthPage,
});

const registerSchema = z.object({
  username: z
    .string()
    .trim()
    .min(3, "Username must be at least 3 characters")
    .max(24, "Username must be 24 characters or fewer")
    .regex(/^[a-zA-Z0-9_]+$/, "Use letters, numbers and underscores only"),
  displayName: z.string().trim().min(2, "Display name is too short").max(48),
  email: z.string().trim().email("Enter a valid email address").max(255),
  password: z.string().min(8, "Use at least 8 characters").max(72),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
});

function isSafePath(value: string | undefined) {
  return Boolean(value && value.startsWith("/") && !value.startsWith("//"));
}

function AuthPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [mode, setMode] = React.useState<"login" | "register">(search.mode ?? "login");
  const [submitting, setSubmitting] = React.useState(false);
  type FieldErrors = Partial<Record<"username" | "displayName" | "email" | "password" | "dateOfBirth", string>>;
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [form, setForm] = React.useState({
    username: "",
    displayName: "",
    email: "",
    password: "",
    dateOfBirth: "",
  });
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [acceptedGuidelines, setAcceptedGuidelines] = React.useState(false);

  React.useEffect(() => {
    if (!loading && user) {
      const target = isSafePath(search.redirect) ? search.redirect! : "/";
      void navigate({ to: target as "/", replace: true });
    }
  }, [user, loading, navigate, search.redirect]);

  function update(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    if (mode === "login") {
      const parsed = z
        .object({ email: z.string().trim().email("Enter a valid email"), password: z.string().min(1, "Enter your password") })
        .safeParse({ email: form.email, password: form.password });
      if (!parsed.success) {
        setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path[0], i.message])) as FieldErrors);
        return;
      }
      setSubmitting(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: parsed.data.email,
        password: parsed.data.password,
      });
      setSubmitting(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Welcome back");
      return;
    }

    const parsed = registerSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path[0], i.message])) as FieldErrors);
      return;
    }
    if (!acceptedTerms || !acceptedGuidelines) {
      toast.error("Please accept the Terms and Community Guidelines to continue");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          username: parsed.data.username,
          display_name: parsed.data.displayName,
          date_of_birth: parsed.data.dateOfBirth,
          accepted_terms: "true",
          accepted_guidelines: "true",
        },
      },
    });
    setSubmitting(false);

    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Account created. Check your inbox to verify your email.");
  }

  async function handleGoogle() {
    setSubmitting(true);
    const result = (await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    })) as { error?: { message?: string } | Error; redirected?: boolean };
    if (result.error) {
      setSubmitting(false);
      toast.error("Google sign-in failed", { description: result.error.message });
      return;
    }
    if (result.redirected) return;
    setSubmitting(false);
    toast.success("Welcome back");
  }

  async function handleForgotPassword() {
    if (!form.email) {
      toast.error("Enter your email address first");
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Password reset link sent");
  }

  return (
    <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-4 py-12">
      <div className="absolute inset-0 -z-10 bg-hero-glow" aria-hidden="true" />
      <div className="rounded-[2rem] border border-border/70 bg-gradient-card p-7 shadow-lift sm:p-9">
        <h1 className="font-display text-3xl text-foreground">
          {mode === "login" ? "Welcome back" : "Join Quibblies"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {mode === "login"
            ? "Sign in to keep reading and writing."
            : "Create a free account to publish, follow and collect stories."}
        </p>

        <form onSubmit={handleSubmit} className="mt-7 space-y-4" noValidate>
          {mode === "register" ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={form.username}
                  onChange={(e) => update("username", e.target.value)}
                  placeholder="nightwriter"
                  autoComplete="username"
                  aria-invalid={Boolean(errors.username)}
                />
                {errors.username ? (
                  <p className="text-xs text-destructive">{errors.username}</p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="displayName">Display name</Label>
                <Input
                  id="displayName"
                  value={form.displayName}
                  onChange={(e) => update("displayName", e.target.value)}
                  placeholder="Ada Nightwriter"
                  aria-invalid={Boolean(errors.displayName)}
                />
                {errors.displayName ? (
                  <p className="text-xs text-destructive">{errors.displayName}</p>
                ) : null}
              </div>
            </>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              autoComplete="email"
              aria-invalid={Boolean(errors.email)}
            />
            {errors.email ? <p className="text-xs text-destructive">{errors.email}</p> : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              aria-invalid={Boolean(errors.password)}
            />
            {errors.password ? (
              <p className="text-xs text-destructive">{errors.password}</p>
            ) : null}
          </div>

          {mode === "register" ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="dob">Date of birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(e) => update("dateOfBirth", e.target.value)}
                  aria-invalid={Boolean(errors.dateOfBirth)}
                />
                <p className="text-xs text-muted-foreground">
                  Kept private — never shown on your profile.
                </p>
                {errors.dateOfBirth ? (
                  <p className="text-xs text-destructive">{errors.dateOfBirth}</p>
                ) : null}
              </div>

              <div className="space-y-3 rounded-2xl border border-border/70 bg-surface/50 p-4">
                <label className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Checkbox
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(v === true)}
                    aria-label="Accept terms"
                  />
                  <span>
                    I agree to the{" "}
                    <Link to="/terms" className="text-primary-glow hover:underline">
                      Terms
                    </Link>
                    .
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Checkbox
                    checked={acceptedGuidelines}
                    onCheckedChange={(v) => setAcceptedGuidelines(v === true)}
                    aria-label="Accept community guidelines"
                  />
                  <span>
                    I agree to the{" "}
                    <Link to="/guidelines" className="text-primary-glow hover:underline">
                      Community Guidelines
                    </Link>
                    .
                  </span>
                </label>
              </div>
            </>
          ) : null}

          <Button type="submit" variant="hero" size="lg" className="w-full" disabled={submitting}>
            {submitting ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-border/70" aria-hidden="true" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
          <span className="h-px flex-1 bg-border/70" aria-hidden="true" />
        </div>

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="mt-4 w-full"
          onClick={handleGoogle}
          disabled={submitting}
        >
          Continue with Google
        </Button>


        <div className="mt-6 flex flex-col gap-2 text-center text-sm text-muted-foreground">
          {mode === "login" ? (
            <button
              type="button"
              onClick={handleForgotPassword}
              className="transition-smooth hover:text-foreground"
            >
              Forgot your password?
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="transition-smooth hover:text-foreground"
          >
            {mode === "login"
              ? "New to Quibblies? Create an account"
              : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
